import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { ApiResponseStatus, LeaveStatus, UserRoles } from 'models/enums';
import { LeaveInfo } from 'models/models';
import moment from 'moment';
import { request } from 'services/apis';
import { toast } from 'react-toastify';

const getLeaveApprovalToastMessage = (
  status: number,
  options?: { isCancellation?: boolean; userRole?: string }
): string => {
  const { isCancellation, userRole } = options ?? {};

  if (isCancellation) {
    if (status === LeaveStatus.REJECTED) {
      return 'Leave cancellation rejected successfully';
    }
    if (status === LeaveStatus.APPROVED) {
      if (userRole === UserRoles.ADMIN) {
        return 'Leave cancellation approved successfully';
      }
      return 'Leave cancellation approved successfully. Awaiting HR approval.';
    }
  }

  if (status === LeaveStatus.APPROVED) {
    return 'Leave approved successfully';
  }
  if (status === LeaveStatus.REJECTED) {
    return 'Leave rejected successfully';
  }
  return 'Leave approval updated successfully';
};

const getHrLeaveStatusToastMessage = (
  status: string,
  isCancellation: boolean
): string => {
  if (!isCancellation) {
    return status === 'Approved'
      ? 'Leave approved successfully'
      : status === 'Rejected'
      ? 'Leave rejected successfully'
      : 'Leave status updated successfully';
  }

  return status === 'Approved'
    ? 'Leave cancellation approved successfully'
    : status === 'Rejected'
    ? 'Leave cancellation rejected successfully'
    : 'Leave cancellation updated successfully';
};

// --- API Response Types ---

export interface AllocationLeaves {
  leaveType: string;
  pendingCount: number;
  remainingCount: number;
  totalCount: number;
  usedCount: number;
  utilizedCount: number;
}

export interface LeaveSummaryItem {
  aboutLeave: string;
  adminComment: string | null;
  attachments: any[];
  checkedBy: string | null;
  checkedDate: string | null;
  employeeName: string;
  employeeEmail: string;
  endDate: string;
  leaveId: string;
  leaveSession: string;
  leaveStatus: string;
  leaveType: string;
  noOfdays: number;
  reason: string;
  requestedDate: string;
  startDate: string;
}

export interface LeaveSummary {
  allocationLeaves: AllocationLeaves[];
  leaveSummary: LeaveSummaryItem[];
  totalApprovedLeaveCount: number;
  totalLeaveCount: number;
  totalPendingLeaveCount: number;
  totalRemainingLeaveCount: number;
  totalUsedLeaveCount: number;
}

export interface LeaveApproval {
  id: string;
  leaveId: string;
  approverId: string;
  approvalDate: string;
  status: number;
  comments: string | null;
  approvalType?: number;
  cancellationReason?: string | null;
}

export interface LeaveRequestItem {
  leaveDetail: LeaveApprovalResponse['leaveDetail'];
  leaveApprovals: LeaveApproval[];
  requesterNickName: string;
  updatedAsTicks: string;
}

export interface PaginatedLeaveRequestsResponse {
  items: LeaveRequestItem[];
  pageNumber: number;
  pageSize: number;
  totalCount: number;
  totalPages: number;
}

// --- Types & Interfaces ---
export interface AttachmentDTO {
  id: string;
  name: string;
}

export interface LeaveApprovalResponse {
  leaveApproval: {
    id: string;
    leaveId: string;
    approverId: string;
    approvalDate: string;
    status: number;
    comments: string | null;
    approvalType?: number;
    cancellationReason?: string | null;
  };
  leaveDetail: {
    detailsId: string;
    upn: string;
    leaveTypeId: string;
    startDate: string;
    endDate: string;
    leaveSession: number;
    leaveStatus: number;
    reason: string;
    requestedDate: string;
    updated: string;
    checkedDate: string | null;
    noOfDays: number;
    checkedBy: string | null;
    adminReason: string | null;
    isBackDated: boolean;
    leaveType: string | null;
    medicalAttachments: AttachmentDTO[];
  };
  requestedPersonNickName: string;
  attachments: AttachmentDTO[];
}

export interface LeaveTypeResponse {
  leaveTypeID: string;
  leaveType: string;
  aboutLeave: string;
}

export interface LeaveState extends LeaveInfo {
  leaveApprovals: LeaveApprovalResponse[];
  // For all leave requests with approvals (paginated)
  allLeaveRequests: LeaveRequestItem[];
  allLeaveRequestsLoading: boolean;
  allLeaveRequestsError: string | null;
  allLeaveRequestsTotalPages: number;
  allLeaveRequestsTotalCount: number;
}

// --- Initial State ---

const initialState: LeaveState = {
  summary: {
    allocationLeaves: [],
    leaveSummary: [],
    totalApprovedLeaveCount: 0,
    totalLeaveCount: 0,
    totalPendingLeaveCount: 0,
    totalRemainingLeaveCount: 0,
    totalUsedLeaveCount: 0
  },
  summaryDuplicate: {},
  // for filter refactoring
  filteredSummary: {},
  // for paginate refactoring
  paginateSummary: {},
  request: {},
  requests: [],
  holidays: [],
  status: '',
  masterDataStatus: '',
  updateLeaveStatusState: '',
  validateLeave: {},
  error: false,
  // for pagination
  activePage: 1,
  rowsPerPage: 20,
  totalPages: 1,
  totalLeaves: 0,
  // for sorting
  orderAsc: 1,
  orderBy: '',
  leaveTypeInfo: [],
  // for new search and filter
  filterType: '',
  searchKeyWord: '',
  // for date range filtering
  startDate: '',
  endDate: '',
  allocationLeaves: [],
  // Store leave approvals fetched by UPN
  leaveApprovals: [],
  // For all leave requests with approvals (paginated)
  allLeaveRequests: [],
  allLeaveRequestsLoading: false,
  allLeaveRequestsError: null,
  allLeaveRequestsTotalPages: 1,
  allLeaveRequestsTotalCount: 0
};

// --- Thunks ---
export const getLeaveApprovals = createAsyncThunk(
  'leaves/getLeaveApprovals',
  async (upn: string) => {
    const encodedUpn = encodeURIComponent(upn);
    const response = await request.get<LeaveApprovalResponse[]>(
      `/Leave/approvals/${encodedUpn}`
    );
    return response.data;
  }
);

// Thunk to fetch all leave requests with approvals (paginated, filtered)
export const getAllLeaveRequests = createAsyncThunk<
  PaginatedLeaveRequestsResponse,
  {
    pageNumber: number;
    pageSize: number;
    statusFilter: string;
    search?: string;
  },
  { rejectValue: string }
>('leaves/getAllLeaveRequests', async (params, { rejectWithValue }) => {
  const { pageNumber, pageSize, statusFilter, search } = params;
  const statusParam =
    statusFilter === 'all' ? '' : `&status=${statusFilter}`;
  const searchParam =
    search && search.trim()
      ? `&search=${encodeURIComponent(search.trim())}`
      : '';
  try {
    const res = await request.get<PaginatedLeaveRequestsResponse>(
      `/Leave/leaves-with-approvals?pageNumber=${pageNumber}&pageSize=${pageSize}${statusParam}${searchParam}`
    );
    if (!res.data) throw new Error('Failed to fetch');
    return res.data;
  } catch (err: any) {
    toast.error('Failed to fetch leave requests');
    return rejectWithValue(err.message || 'Failed to fetch leave requests');
  }
});

export const getAssignedLeaveRequests = createAsyncThunk<
  PaginatedLeaveRequestsResponse,
  {
    pageNumber: number;
    pageSize: number;
    statusFilter: string;
    search?: string;
  },
  { rejectValue: string }
>('leaves/getAssignedLeaveRequests', async (params, { rejectWithValue }) => {
  const { pageNumber, pageSize, statusFilter, search } = params;
  const statusParam =
    statusFilter === 'all' ? '' : `&status=${statusFilter}`;
  const searchParam =
    search && search.trim()
      ? `&search=${encodeURIComponent(search.trim())}`
      : '';
  try {
    const res = await request.get<PaginatedLeaveRequestsResponse>(
      `/Leave/assigned-leaves-with-approvals?pageNumber=${pageNumber}&pageSize=${pageSize}${statusParam}${searchParam}`
    );
    if (!res.data) throw new Error('Failed to fetch');
    return res.data;
  } catch (err: any) {
    toast.error('Failed to fetch assigned leave requests');
    return rejectWithValue(err.message || 'Failed to fetch assigned leave requests');
  }
});

export const getLeaveTypes = createAsyncThunk<LeaveTypeResponse[]>(
  'leaves/getLeaveTypes',
  async () => {
    try {
      const response = await request.get<LeaveTypeResponse[]>(
        '/Leave/leave-types'
      );
      return response.data;
    } catch (error) {
      console.log(error);
      return [];
    }
  }
);

export const getLeaveSummary = createAsyncThunk(
  'leaves/getLeaveSummary',
  async () => {
    try {
      const response = await request.get<LeaveSummary>('/Leave/leave-summary');
      return response.data;
    } catch (error) {
      console.error(error);
    }
  }
);

export const getPublicHolidays = createAsyncThunk(
  'leaves/getPublicHolidays',
  async () => {
    try {
      const response = await request.get('/Holiday/public-holidays');
      return response.data;
    } catch (error) {
      console.error(error);
    }
  }
);

export const saveLeaveRequest = createAsyncThunk(
  'leaves/saveLeaveRequest',
  async (leaveRequest: FormData, { rejectWithValue }) => {
    try {
      const response: any = await request.post('/Leave/save', leaveRequest);

      toast.success(
        response.data?.message ?? 'Leave request submitted successfully'
      );
      return response.data;
    } catch (error: any) {
      const data = error.response?.data;
      if (error.response?.status === 401) {
        toast.error('You are not signed in');
        return rejectWithValue(data ?? 'Failed to submit leave');
      }
      if (data?.attachments === null) {
        toast.error('Medical leaves require at least one attachment');
      } else if (data?.message) {
        toast.error(data.message, {
          autoClose: 8000,
          style: { height: 'auto' },
          bodyStyle: { whiteSpace: 'pre-wrap' },
        });
      } else if (data?.status) {
        toast.error(data.status);
      } else {
        toast.error('Failed to submit leave request');
      }
      return rejectWithValue(data ?? 'Failed to submit leave');
    }
  }
);

// delete request
export const deleteLeaveRequest = createAsyncThunk(
  'leaves/deleteLeaveRequest',
  async (id: any) => {
    try {
      const response: any = await request.delete(`/Leave/delete-request/${id}`);
      if (response.data === true) {
        toast.success('Leave request deleted successfully!');
      }
      return response.data;
    } catch (error) {
      toast.error('Could not delete the leave request!');
      console.error(error);
    }
  }
);

export const requestLeaveCancellation = createAsyncThunk(
  'leaves/requestLeaveCancellation',
  async (
    data: { leaveId: string; reason: string },
    { rejectWithValue }
  ) => {
    try {
      const response: any = await request.post(
        `/Leave/request-cancellation/${data.leaveId}`,
        { reason: data.reason }
      );
      toast.success(
        response.data?.message ??
          'Leave cancellation request submitted successfully. Your project manager must approve first, then HR.'
      );
      return response.data;
    } catch (error: any) {
      if (error.response?.status === 403) {
        toast.error('You are not authorized to cancel this leave request.');
      } else if (error.response?.data?.message) {
        toast.error(error.response.data.message);
      } else {
        toast.error('Failed to submit cancellation request.');
      }
      return rejectWithValue(error.response?.data ?? 'Failed to submit cancellation request');
    }
  }
);

// update request - user
export const updateLeaveRequest = createAsyncThunk(
  'leaves/updateLeaveRequest',
  async (data: any, thunkApi) => {
    const { id, formData } = data;
    try {
      const response: any = await request.put(
        `/Leave/update-request/${id}`,
        formData
      );
      toast.success(response.data.message);
      if (response.status >= 200 && response.status < 300) {
        return response.data;
      }
    } catch (error: any) {
      if (error.response.data.message) toast.error(error.response.data.message);

      if (error.response.data.errors.Files) {
        error.response.data.errors.Files.map((file: any) => toast.error(file));
      } else if (error.response.data.message === undefined)
        toast.error(error.response.data);
      else toast.error(error.response.data.message);

      if (error.response.status === 401) toast.error('You are not signed in');
      console.log(error);
    }
  }
);

// update status of leave request - admin
export const updateLeaveRequestStatus = createAsyncThunk(
  'leaves/updateLeaveRequestStatus',
  async (
    payload: FormData | { formData: FormData; isCancellation?: boolean },
    thunkApi
  ) => {
    const formData = payload instanceof FormData ? payload : payload.formData;
    const isCancellation =
      payload instanceof FormData ? false : Boolean(payload.isCancellation);
    const statusValue = formData.get('status')?.toString() ?? '';

    try {
      const response: any = await request.put(
        `/leave/update-status`,
        formData
      );
      toast.success(
        getHrLeaveStatusToastMessage(statusValue, isCancellation)
      );

      if (response.status >= 200 && response.status < 300) {
        return response.data;
      }
    } catch (error: any) {
      if (error.response.data.message === undefined)
        toast.error(error.response.data);
      else toast.error(error.response.data.message);

      if (error.response.status === 401) toast.error('You are not signed in');
    }
  }
);

export const validateLeave = createAsyncThunk(
  'leaves/validateLeave',
  async (requestObj: any, { rejectWithValue }) => {
    try {
      const response: any = await request.get(
        `/leave/latest-request/${requestObj.leaveId}/${requestObj.updated}`
      );

      if (response.status === 200) {
        return response.data;
      }
    } catch (error: any) {
      if (error.response?.data?.message === undefined)
        toast.error(error.response.data);
      else toast.error(error.response?.data?.message);

      if (error.response?.status === 401) toast.error('You are not signed in');
      return rejectWithValue(error.response?.data ?? 'Failed to load leave');
    }
  }
);

export const updateLeaveApprovals = createAsyncThunk(
  'leaves/updateLeaveApprovals',
  async (
    data: {
      id: string;
      status: number;
      comments: string;
      isCancellation?: boolean;
      userRole?: string;
    },
    { rejectWithValue }
  ) => {
    try {
      const response: any = await request.put(
        `/LeaveApproval/UpdateLeaveApproval?id=${data.id}`,
        { status: data.status, comments: data.comments }
      );
      toast.success(
        getLeaveApprovalToastMessage(data.status, {
          isCancellation: data.isCancellation,
          userRole: data.userRole
        })
      );
      if (response.status >= 200 && response.status < 300) {
        return response.data;
      }
    } catch (error: any) {
      if (error.response?.status === 403) {
        toast.error(
          data.isCancellation
            ? 'You are not authorized to act on this leave cancellation.'
            : 'You are not authorized to approve this leave request.'
        );
      } else if (error.response?.data?.message === undefined)
        toast.error(error.response?.data);
      else toast.error(error.response?.data?.message);

      if (error.response?.status === 401) toast.error('You are not signed in');
      return rejectWithValue(error.response?.data ?? 'Failed to update approval');
    }
  }
);

const leaveSlice = createSlice({
  name: 'leaves',
  initialState,
  reducers: {
    clearValidateResponse: (state) => ({
      ...state,
      validateLeave: {},
      updateLeaveStatusState: ''
    }),
    // Pagination
    paginateLeaves: (state, { payload }) => {
      state.rowsPerPage = payload;
      state.activePage = 1;
      state.totalPages = state.paginateSummary.leaveSummary
        ? Math.ceil(
            state.paginateSummary.leaveSummary.length / state.rowsPerPage
          )
        : 1;
      if (state.totalPages === 0) {
        state.totalPages = 1;
      }
      state.summary = {
        ...state.summary,
        leaveSummary: state.paginateSummary.leaveSummary
          ? state.paginateSummary.leaveSummary.slice(
              (state.activePage - 1) * state.rowsPerPage,
              state.activePage * state.rowsPerPage
            )
          : []
      };
    },
    navigateLeaves: (state, { payload }) => {
      state.activePage = payload;
      state.summary = {
        ...state.summary,
        leaveSummary: state.paginateSummary.leaveSummary.slice(
          (state.activePage - 1) * state.rowsPerPage,
          state.activePage * state.rowsPerPage
        )
      };
    },
    // Pagination

    // Newest filter method
    filterLeaves: (state, { payload }) => {
      state.filterType =
        payload.filterByType !== null ? payload.filterByType : 'All';
      state.startDate =
        payload.startDate !== null
          ? payload.startDate
          : moment(new Date(0)).format('YYYY-MM-DD');
      state.endDate =
        payload.endDate !== null
          ? payload.endDate
          : moment(new Date(8640000000000000)).format('YYYY-MM-DD');

      state.filteredSummary = {
        ...state.filteredSummary,
        leaveSummary: state.summaryDuplicate.leaveSummary
          .filter((item: any) =>
            state.filterType !== 'All'
              ? item.leaveType === state.filterType
              : state.summaryDuplicate.leaveSummary
          )
          .filter((item: any) =>
            moment(item.startDate).isBetween(
              moment(state.startDate),
              moment(state.endDate),
              undefined,
              '[]'
            )
          )
      };

      // setting the total number of pages as according to the result of filtering by type
      state.totalPages = state.filteredSummary.leaveSummary
        ? Math.ceil(
            state.filteredSummary.leaveSummary.length / state.rowsPerPage
          )
        : 1;

      // setting the total pages to 1, when the filtered list is empty
      if (state.totalPages === 0) state.totalPages = 1;

      // extra conditions (i) search => filter
      if (state.searchKeyWord === '') {
        state.summary = {
          ...state.summary,
          leaveSummary: state.filteredSummary.leaveSummary
        };
      } else {
        state.summary = {
          ...state.summary,
          leaveSummary: state.filteredSummary.leaveSummary.filter((item: any) =>
            item.reason
              .toLowerCase()
              .includes(state.searchKeyWord.toLowerCase())
          )
        };

        // setting the total pages in case a search keyword is entered
        state.totalPages = state.summary.leaveSummary
          ? Math.ceil(state.summary.leaveSummary.length / state.rowsPerPage)
          : 1;
      }

      // setting list to be used for pagination
      state.paginateSummary = {
        ...state.paginateSummary,
        leaveSummary: state.summary.leaveSummary
      };

      if (state.rowsPerPage !== null) {
        state.summary = {
          ...state.summary,
          leaveSummary: state.summary.leaveSummary.slice(
            (state.activePage - 1) * state.rowsPerPage,
            state.activePage * state.rowsPerPage
          )
        };
      }
    },
    // end of filter method

    // start of search method
    searchLeaves: (state, { payload }) => {
      state.searchKeyWord = payload !== null ? payload : '';
      state.summary = {
        ...state.summary,
        leaveSummary:
          state.searchKeyWord !== ''
            ? state.filteredSummary.leaveSummary.filter((item: any) =>
                item.reason
                  .toLowerCase()
                  .includes(state.searchKeyWord.toLowerCase())
              )
            : state.filteredSummary.leaveSummary
      };

      // setting list to be used for pagination
      state.paginateSummary = {
        ...state.paginateSummary,
        leaveSummary: state.summary.leaveSummary
      };

      // setting the total number of pages as according to the result of filtering by type
      state.totalPages = state.filteredSummary.leaveSummary
        ? Math.ceil(
            state.filteredSummary.leaveSummary.length / state.rowsPerPage
          )
        : 1;

      // setting the total number of pages when the list
      if (state.filterType !== null && state.searchKeyWord !== null) {
        state.totalPages = state.paginateSummary.leaveSummary
          ? Math.ceil(
              state.paginateSummary.leaveSummary.length / state.rowsPerPage
            )
          : 1;
      }

      // setting the total number of pages to 1 in case it was set to zero
      if (state.totalPages === 0) state.totalPages = 1;

      if (state.rowsPerPage !== null) {
        state.summary = {
          ...state.summary,
          leaveSummary: state.summary.leaveSummary.slice(
            (state.activePage - 1) * state.rowsPerPage,
            state.activePage * state.rowsPerPage
          )
        };
      }
    },
    // end of search method

    sortLeaves: (state, { payload }) => {
      state.orderAsc *= -1;

      switch (payload) {
        case 'reason':
        case 'startDate':
        case 'endDate':
        case 'leaveStatus':
        case 'adminComment':
        case 'leaveSession':
        case 'noOfdays':
          state.summary = {
            ...state.summary,
            leaveSummary: state.summary.leaveSummary
              ? state.summary.leaveSummary.sort(
                  (a: any, b: any) =>
                    a[payload]
                      .toString()
                      .localeCompare(b[payload].toString(), 'en', {
                        numeric: true
                      }) * state.orderAsc
                )
              : []
          };
          break;
        default:
          break;
      }
    },
    resetFilter: (state) => {
      state.filterType = 'All';
      state.startDate = '';
      state.endDate = '';
      // set the filteredSummary list to hold all leaves
      state.filteredSummary = {
        ...state.filteredSummary,
        leaveSummary: state.summaryDuplicate.leaveSummary
      };

      // do the search operation on unfiltered leave list, if a search keyword is given
      state.summary = {
        ...state.summary,
        leaveSummary:
          state.searchKeyWord !== ''
            ? state.summary.leaveSummary.filter((item: any) =>
                item.reason
                  .toLowerCase()
                  .includes(state.searchKeyWord.toLowerCase())
              )
            : state.filteredSummary.leaveSummary
      };

      state.paginateSummary = {
        ...state.paginateSummary,
        leaveSummary: state.summary.leaveSummary
      };

      if (state.rowsPerPage) {
        state.summary = {
          ...state.summary,
          leaveSummary: state.filteredSummary.leaveSummary
            .filter((item: any) =>
              item.reason
                .toLowerCase()
                .includes(state.searchKeyWord.toLowerCase())
            )
            .slice(
              (state.activePage - 1) * state.rowsPerPage,
              state.activePage * state.rowsPerPage
            )
        };

        state.totalPages = state.paginateSummary.leaveSummary
          ? Math.ceil(
              state.paginateSummary.leaveSummary.length / state.rowsPerPage
            )
          : 1;
      } else {
        state.summary = {
          ...state.summary,
          leaveSummary: state.filteredSummary.leaveSummary.filter((item: any) =>
            item.reason
              .toLowerCase()
              .includes(state.searchKeyWord.toLowerCase())
          )
        };
      }
    }
  },
  extraReducers: (builder) => {
    // All Leave Requests with Approvals (paginated)
    builder.addCase(getAllLeaveRequests.pending, (state) => {
      state.allLeaveRequestsLoading = true;
      state.allLeaveRequestsError = null;
    });
    builder.addCase(getAllLeaveRequests.fulfilled, (state, { payload }) => {
      state.allLeaveRequests = payload.items || [];
      state.allLeaveRequestsTotalPages = payload.totalPages || 1;
      state.allLeaveRequestsTotalCount = payload.totalCount || 0;
      state.allLeaveRequestsLoading = false;
      state.allLeaveRequestsError = null;
    });
    builder.addCase(getAllLeaveRequests.rejected, (state, { payload }) => {
      state.allLeaveRequestsLoading = false;
      state.allLeaveRequestsError =
        (payload as string) || 'Failed to fetch leave requests';
    });
    builder.addCase(getAssignedLeaveRequests.pending, (state) => {
      state.allLeaveRequestsLoading = true;
      state.allLeaveRequestsError = null;
    });
    builder.addCase(getAssignedLeaveRequests.fulfilled, (state, { payload }) => {
      state.allLeaveRequests = payload.items || [];
      state.allLeaveRequestsTotalPages = payload.totalPages || 1;
      state.allLeaveRequestsTotalCount = payload.totalCount || 0;
      state.allLeaveRequestsLoading = false;
      state.allLeaveRequestsError = null;
    });
    builder.addCase(getAssignedLeaveRequests.rejected, (state, { payload }) => {
      state.allLeaveRequestsLoading = false;
      state.allLeaveRequestsError =
        (payload as string) || 'Failed to fetch assigned leave requests';
    });
    builder.addCase(getLeaveTypes.pending, (state) => {
      state.masterDataStatus = ApiResponseStatus.pending;
      state.status = ApiResponseStatus.pending;
      state.error = false;
    });
    builder.addCase(getLeaveTypes.fulfilled, (state, { payload }) => {
      state.leaveTypeInfo = payload;
      state.masterDataStatus = ApiResponseStatus.fulfilled;
      state.status = ApiResponseStatus.fulfilled;
      state.error = false;
    });
    builder.addCase(getLeaveTypes.rejected, (state) => {
      state.masterDataStatus = ApiResponseStatus.rejected;
      state.status = ApiResponseStatus.rejected;
      state.error = true;
    });

    builder.addCase(getPublicHolidays.pending, (state) => {
      state.masterDataStatus = ApiResponseStatus.pending;
      state.status = ApiResponseStatus.pending;
      state.error = false;
    });
    builder.addCase(getPublicHolidays.fulfilled, (state, { payload }) => {
      state.holidays = payload;
      state.masterDataStatus = ApiResponseStatus.fulfilled;
      state.status = ApiResponseStatus.fulfilled;
      state.error = false;
    });
    builder.addCase(getPublicHolidays.rejected, (state) => {
      state.masterDataStatus = ApiResponseStatus.rejected;
      state.status = ApiResponseStatus.rejected;
      state.error = true;
    });

    builder.addCase(getLeaveSummary.pending, (state) => {
      state.masterDataStatus = ApiResponseStatus.pending;
      state.status = ApiResponseStatus.pending;
      state.error = false;
    });
    builder.addCase(getLeaveSummary.fulfilled, (state, { payload }: any) => {
      state.summary = payload;
      state.summaryDuplicate = payload;
      state.filteredSummary = payload;
      state.allocationLeaves = payload?.allocationLeaves;
      state.activePage = 1;
      state.rowsPerPage = 20;
      state.totalPages = 1;
      state.totalLeaves = 0;
      state.paginateSummary = payload;
      state.totalLeaves = state.summary?.leaveSummary.length;
      state.masterDataStatus = ApiResponseStatus.fulfilled;
      state.status = ApiResponseStatus.fulfilled;
      state.error = false;
    });
    builder.addCase(getLeaveSummary.rejected, (state) => {
      state.masterDataStatus = ApiResponseStatus.rejected;
      state.status = ApiResponseStatus.rejected;
      state.error = true;
    });
    builder.addCase(saveLeaveRequest.pending, (state) => {
      state.status = ApiResponseStatus.pending;
      state.error = false;
    });
    builder.addCase(saveLeaveRequest.fulfilled, (state, payload) => {
      state.requests = payload;
      state.status = ApiResponseStatus.fulfilled;
      state.error = false;
    });
    builder.addCase(saveLeaveRequest.rejected, (state) => {
      state.status = ApiResponseStatus.rejected;
      state.error = true;
    });
    builder.addCase(updateLeaveRequest.pending, (state) => {
      state.status = ApiResponseStatus.pending;
      state.error = false;
    });
    builder.addCase(updateLeaveRequest.fulfilled, (state, payload) => {
      state.requests = payload;
      state.status = ApiResponseStatus.fulfilled;
      state.error = false;
    });
    builder.addCase(updateLeaveRequest.rejected, (state) => {
      state.status = ApiResponseStatus.rejected;
      state.error = true;
    });
    builder.addCase(deleteLeaveRequest.pending, (state) => {
      state.status = ApiResponseStatus.pending;
      state.error = false;
    });
    builder.addCase(deleteLeaveRequest.fulfilled, (state, payload) => {
      state.requests = payload;
      state.status = ApiResponseStatus.fulfilled;
      state.error = false;
    });
    builder.addCase(deleteLeaveRequest.rejected, (state) => {
      state.status = ApiResponseStatus.rejected;
      state.error = true;
    });

    // update leave request status
    builder.addCase(updateLeaveRequestStatus.pending, (state) => {
      state.status = ApiResponseStatus.pending;
      state.error = false;
    });
    builder.addCase(updateLeaveRequestStatus.fulfilled, (state, payload) => {
      state.status = ApiResponseStatus.fulfilled;
      state.updateLeaveStatusState = ApiResponseStatus.fulfilled;
      state.error = false;
    });
    builder.addCase(updateLeaveRequestStatus.rejected, (state) => {
      state.status = ApiResponseStatus.rejected;
      state.error = true;
    });

    builder.addCase(validateLeave.pending, (state) => {
      state.status = ApiResponseStatus.pending;
      state.error = false;
    });
    builder.addCase(validateLeave.fulfilled, (state, payload) => {
      state.status = ApiResponseStatus.fulfilled;
      state.validateLeave = payload.payload;
      state.error = false;
    });
    builder.addCase(validateLeave.rejected, (state) => {
      state.status = ApiResponseStatus.rejected;
      state.error = true;
    });

    // Store leave approvals fetched by UPN
    builder.addCase(getLeaveApprovals.pending, (state) => {
      state.status = ApiResponseStatus.pending;
      state.error = false;
    });
    builder.addCase(getLeaveApprovals.fulfilled, (state, { payload }) => {
      state.leaveApprovals = payload;
      state.status = ApiResponseStatus.fulfilled;
      state.error = false;
    });
    builder.addCase(getLeaveApprovals.rejected, (state) => {
      state.status = ApiResponseStatus.rejected;
      state.error = true;
    });

    // Update leave approvals (approve/reject)
    builder.addCase(updateLeaveApprovals.pending, (state) => {
      state.status = ApiResponseStatus.pending;
      state.error = false;
    });
    builder.addCase(updateLeaveApprovals.fulfilled, (state, { payload }) => {
      state.status = ApiResponseStatus.fulfilled;
      state.error = false;
    });
    builder.addCase(updateLeaveApprovals.rejected, (state) => {
      state.status = ApiResponseStatus.rejected;
      state.error = true;
    });
  }
});

const { actions, reducer } = leaveSlice;

export const {
  paginateLeaves,
  navigateLeaves,
  sortLeaves,
  resetFilter,
  filterLeaves,
  searchLeaves,
  clearValidateResponse
} = actions;

export default reducer;
